import { useState, useEffect, useCallback } from 'react'
import { LayoutGrid, RefreshCw, Star, Filter } from 'lucide-react'
import { clsx } from 'clsx'
import { formatDistanceToNow } from 'date-fns'
import { api } from '../lib/api'

function relativeTime(ts: string): string {
  if (!ts) return ''
  try { return formatDistanceToNow(new Date(ts), { addSuffix: false }) + ' ago' } catch { return ts }
}

// ── Cloud Vendor Data ────────────────────────────────────────────────────────

interface VendorRow {
  category: string
  vendors: { name: string; service: string; notes: string; best?: boolean }[]
}

const CLOUD_VENDORS = ['AWS', 'Azure', 'GCP']

const CLOUD_DATA: VendorRow[] = [
  {
    category: 'Compute',
    vendors: [
      { name: 'AWS', service: 'EC2 / Graviton', notes: 'Widest instance selection, custom Arm chips', best: true },
      { name: 'Azure', service: 'Virtual Machines', notes: 'Strong Windows/hybrid integration' },
      { name: 'GCP', service: 'Compute Engine', notes: 'Per-second billing, live migration' },
    ],
  },
  {
    category: 'Storage',
    vendors: [
      { name: 'AWS', service: 'S3', notes: 'Market leader, 11 nines durability', best: true },
      { name: 'Azure', service: 'Blob Storage', notes: 'Tiered storage, Azure AD integration' },
      { name: 'GCP', service: 'Cloud Storage', notes: 'Unified API, autoclass tiering' },
    ],
  },
  {
    category: 'Database',
    vendors: [
      { name: 'AWS', service: 'RDS / Aurora / DynamoDB', notes: 'Broadest managed DB portfolio', best: true },
      { name: 'Azure', service: 'Cosmos DB / SQL DB', notes: 'Multi-model Cosmos, global distribution' },
      { name: 'GCP', service: 'Cloud SQL / Spanner / Firestore', notes: 'Spanner = globally consistent' },
    ],
  },
  {
    category: 'Kubernetes',
    vendors: [
      { name: 'AWS', service: 'EKS', notes: 'Largest ecosystem, Fargate serverless pods' },
      { name: 'Azure', service: 'AKS', notes: 'Free control plane, tight Azure AD RBAC' },
      { name: 'GCP', service: 'GKE', notes: 'Built by K8s creators, Autopilot mode', best: true },
    ],
  },
  {
    category: 'Serverless',
    vendors: [
      { name: 'AWS', service: 'Lambda', notes: 'Pioneer, massive trigger ecosystem', best: true },
      { name: 'Azure', service: 'Functions', notes: 'Durable functions, .NET optimised' },
      { name: 'GCP', service: 'Cloud Functions / Cloud Run', notes: 'Cloud Run = container-native serverless' },
    ],
  },
  {
    category: 'AI/ML',
    vendors: [
      { name: 'AWS', service: 'SageMaker / Bedrock', notes: 'End-to-end ML platform, Bedrock for GenAI' },
      { name: 'Azure', service: 'Azure OpenAI / ML Studio', notes: 'Exclusive OpenAI access', best: true },
      { name: 'GCP', service: 'Vertex AI / TPUs', notes: 'Custom TPUs, Gemini integration' },
    ],
  },
  {
    category: 'CDN',
    vendors: [
      { name: 'AWS', service: 'CloudFront', notes: '600+ PoPs, Lambda@Edge' },
      { name: 'Azure', service: 'Front Door / CDN', notes: 'Integrated WAF + load balancing' },
      { name: 'GCP', service: 'Cloud CDN', notes: 'Leverages Google backbone', best: true },
    ],
  },
  {
    category: 'Security',
    vendors: [
      { name: 'AWS', service: 'GuardDuty / SecurityHub', notes: 'Deep threat detection, broad compliance' },
      { name: 'Azure', service: 'Defender for Cloud', notes: 'Best hybrid/multi-cloud posture', best: true },
      { name: 'GCP', service: 'Security Command Center', notes: 'BeyondCorp zero-trust model' },
    ],
  },
  {
    category: 'Monitoring',
    vendors: [
      { name: 'AWS', service: 'CloudWatch', notes: 'Native but verbose, growing observability suite' },
      { name: 'Azure', service: 'Monitor / App Insights', notes: 'Strong APM, KQL queries' },
      { name: 'GCP', service: 'Cloud Monitoring / Logging', notes: 'Integrated with SRE tooling', best: true },
    ],
  },
]

// ── Security Vendor Data ─────────────────────────────────────────────────────

const SECURITY_VENDORS = ['CrowdStrike', 'Microsoft Defender', 'SentinelOne', 'Palo Alto']

interface SecurityRow {
  category: string
  vendors: { name: string; notes: string; best?: boolean }[]
}

const SECURITY_DATA: SecurityRow[] = [
  {
    category: 'Endpoint (EDR)',
    vendors: [
      { name: 'CrowdStrike', notes: 'Falcon platform, lightweight agent, top MITRE results', best: true },
      { name: 'Microsoft Defender', notes: 'Built into Windows, E5 license, good enough for many' },
      { name: 'SentinelOne', notes: 'Autonomous AI response, Singularity platform' },
      { name: 'Palo Alto', notes: 'Cortex XDR, integrates with firewall telemetry' },
    ],
  },
  {
    category: 'Cloud Security (CNAPP)',
    vendors: [
      { name: 'CrowdStrike', notes: 'Falcon Cloud Security, runtime protection' },
      { name: 'Microsoft Defender', notes: 'Defender for Cloud, best Azure native', best: true },
      { name: 'SentinelOne', notes: 'PingSafe acquisition, growing CNAPP' },
      { name: 'Palo Alto', notes: 'Prisma Cloud, broadest CNAPP feature set' },
    ],
  },
  {
    category: 'SIEM/XDR',
    vendors: [
      { name: 'CrowdStrike', notes: 'LogScale (Humio), fast log analytics' },
      { name: 'Microsoft Defender', notes: 'Sentinel + Defender XDR, unified portal', best: true },
      { name: 'SentinelOne', notes: 'Singularity Data Lake, open XDR' },
      { name: 'Palo Alto', notes: 'XSIAM, AI-driven SOC platform' },
    ],
  },
  {
    category: 'Identity / Zero Trust',
    vendors: [
      { name: 'CrowdStrike', notes: 'Falcon Identity, AD protection' },
      { name: 'Microsoft Defender', notes: 'Entra ID + Conditional Access, deepest identity stack', best: true },
      { name: 'SentinelOne', notes: 'Ranger for network visibility' },
      { name: 'Palo Alto', notes: 'Prisma Access ZTNA, GlobalProtect' },
    ],
  },
  {
    category: 'Pricing Model',
    vendors: [
      { name: 'CrowdStrike', notes: 'Per-endpoint, modular bundles, premium pricing' },
      { name: 'Microsoft Defender', notes: 'Bundled in E5, best value if in MS ecosystem', best: true },
      { name: 'SentinelOne', notes: 'Per-endpoint, competitive pricing, simple tiers' },
      { name: 'Palo Alto', notes: 'Per-asset credits, can be complex' },
    ],
  },
]

// ── Observability Vendor Data ────────────────────────────────────────────────

const OBS_VENDORS = ['Datadog', 'Grafana', 'New Relic', 'Elastic']

const OBS_DATA: SecurityRow[] = [
  {
    category: 'APM',
    vendors: [
      { name: 'Datadog', notes: 'Full-stack APM, auto-instrumentation, broadest language support', best: true },
      { name: 'Grafana', notes: 'Tempo for traces, growing APM via Grafana Cloud' },
      { name: 'New Relic', notes: 'Mature APM, generous free tier, all-in-one pricing' },
      { name: 'Elastic', notes: 'Elastic APM, OpenTelemetry native' },
    ],
  },
  {
    category: 'Logs',
    vendors: [
      { name: 'Datadog', notes: 'Log Management, Flex Logs for cold storage' },
      { name: 'Grafana', notes: 'Loki — label-indexed, cheapest at scale', best: true },
      { name: 'New Relic', notes: 'Logs in context, NRQL queries' },
      { name: 'Elastic', notes: 'Elasticsearch core, most powerful search/aggregation' },
    ],
  },
  {
    category: 'Metrics',
    vendors: [
      { name: 'Datadog', notes: '800+ integrations, custom metrics, Notebooks' },
      { name: 'Grafana', notes: 'Mimir/Prometheus, best dashboards, multi-source', best: true },
      { name: 'New Relic', notes: 'Dimensional metrics, NRQL flexibility' },
      { name: 'Elastic', notes: 'TSDB in Elasticsearch, Lens visualisations' },
    ],
  },
  {
    category: 'Alerting',
    vendors: [
      { name: 'Datadog', notes: 'Monitors, composite alerts, SLO tracking', best: true },
      { name: 'Grafana', notes: 'Unified alerting, multi-datasource rules' },
      { name: 'New Relic', notes: 'Alert conditions + workflows, AI anomaly detection' },
      { name: 'Elastic', notes: 'Watcher + rules, Kibana alerting framework' },
    ],
  },
  {
    category: 'Pricing',
    vendors: [
      { name: 'Datadog', notes: 'Per-host + ingestion, can get expensive at scale' },
      { name: 'Grafana', notes: 'OSS self-host free, Cloud usage-based, cheapest SaaS', best: true },
      { name: 'New Relic', notes: 'Per-user + GB ingested, 100GB/mo free', best: true },
      { name: 'Elastic', notes: 'OSS self-host, Cloud resource-based pricing' },
    ],
  },
]

// ── All categories for filtering ─────────────────────────────────────────────

type Section = 'cloud' | 'security' | 'observability'

const ALL_SECTIONS: { key: Section; label: string; color: string }[] = [
  { key: 'cloud', label: 'Cloud (AWS / Azure / GCP)', color: 'text-cyan' },
  { key: 'security', label: 'Security (EDR / XDR)', color: 'text-rose' },
  { key: 'observability', label: 'Observability', color: 'text-violet' },
]

function ComparisonTable({ vendors, rows, color }: { vendors: string[]; rows: { category: string; vendors: { name: string; service?: string; notes: string; best?: boolean }[] }[]; color: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="border-b border-border">
            <th className="text-[10px] text-text-muted font-semibold py-2 pr-3 w-28">Category</th>
            {vendors.map(v => (
              <th key={v} className={clsx('text-[10px] font-semibold py-2 px-3', color)}>{v}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.category} className="border-b border-border/50 hover:bg-surface-raised/50 transition-colors">
              <td className="text-[11px] font-semibold text-text-primary py-2.5 pr-3 align-top">{row.category}</td>
              {vendors.map(vendorName => {
                const v = row.vendors.find(x => x.name === vendorName)
                if (!v) return <td key={vendorName} className="py-2.5 px-3 text-[10px] text-text-muted">-</td>
                return (
                  <td key={vendorName} className="py-2.5 px-3 align-top">
                    <div className="flex items-start gap-1">
                      {v.best && <Star size={10} className="text-amber shrink-0 mt-0.5" fill="currentColor" />}
                      <div>
                        {'service' in v && v.service && (
                          <span className="text-[10px] font-semibold text-text-primary block">{v.service}</span>
                        )}
                        <span className="text-[10px] text-text-secondary leading-snug block">{v.notes}</span>
                      </div>
                    </div>
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default function VendorComparisonPage() {
  const [filter, setFilter] = useState<Section | 'all'>('all')

  const showCloud = filter === 'all' || filter === 'cloud'
  const showSecurity = filter === 'all' || filter === 'security'
  const showObs = filter === 'all' || filter === 'observability'

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <LayoutGrid size={20} className="text-blue" /> Vendor Comparison
          </h1>
          <p className="text-[10px] text-text-secondary">Side-by-side comparison matrices for key technology categories</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={12} className="text-text-muted" />
          <select title="" value={filter} onChange={e => setFilter(e.target.value as Section | 'all')}
            className="input py-1.5 text-[11px] pr-6">
            <option value="all">All Categories</option>
            {ALL_SECTIONS.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Cloud Providers ────────────────────────────────────────── */}
      {showCloud && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan" />
            Cloud Providers
            <span className="text-[10px] text-text-muted font-normal">AWS vs Azure vs GCP</span>
          </h2>
          <ComparisonTable vendors={CLOUD_VENDORS} rows={CLOUD_DATA} color="text-cyan" />
        </div>
      )}

      {/* ── Security Vendors ───────────────────────────────────────── */}
      {showSecurity && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-rose" />
            Security Platforms
            <span className="text-[10px] text-text-muted font-normal">CrowdStrike vs Defender vs SentinelOne vs Palo Alto</span>
          </h2>
          <ComparisonTable vendors={SECURITY_VENDORS} rows={SECURITY_DATA} color="text-rose" />
        </div>
      )}

      {/* ── Observability Vendors ──────────────────────────────────── */}
      {showObs && (
        <div className="card p-4">
          <h2 className="text-sm font-semibold text-text-primary flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-violet" />
            Observability Platforms
            <span className="text-[10px] text-text-muted font-normal">Datadog vs Grafana vs New Relic vs Elastic</span>
          </h2>
          <ComparisonTable vendors={OBS_VENDORS} rows={OBS_DATA} color="text-violet" />
        </div>
      )}

      {/* Legend */}
      <div className="flex items-center gap-3 text-[10px] text-text-muted">
        <div className="flex items-center gap-1">
          <Star size={10} className="text-amber" fill="currentColor" /> = Recommended / category leader
        </div>
        <span>Data reflects general consensus as of early 2026. Always validate for your specific workloads.</span>
      </div>
    </div>
  )
}
